import CartProductIcon from "../../assets/images/packaged-delivered.png";
import PaymentSuccessIcon from "../../assets/svg/payment-success.svg";
import WarningIcon from "../../assets/svg/warning.svg";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GetAllOrder } from "../../Services/OrderService";
import { getImageUrl, handleImageError } from "../../utils/imageUtils";

const OrderConfirmed = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const orderIdFromState = location.state?.orderId;
    const orderIdFromStorage = localStorage.getItem('orderId');
    const orderId = orderIdFromState || orderIdFromStorage || null;
    const navigate = useNavigate();

    // ✅ Fetch orders
    const fetchOrders = async () => {
        const userId = localStorage.getItem("userId");
        if (!userId) {
            console.error("User ID not found");
            setLoading(false);
            return;
        }
        try {
            const params = { userId: parseInt(userId), orderId: orderId };
            const response = await GetAllOrder(params);
            setOrders(response?.data?.orders || []);
        } catch (error) {
            console.error("Error fetching orders:", error);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Initial Load
    useEffect(() => {
        fetchOrders();
    }, []);

    useEffect(() => {
        // Prevent manual access
        if (!orderId) {
            navigate("/cart", { replace: true });
        }
    }, [orderId]);

    useEffect(() => {
        window.history.pushState(null, "", window.location.href);

        const handlePopState = () => {
            window.history.pushState(null, "", window.location.href);
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    if (loading) {
        return <div className="text-center py-5">Loading order details...</div>;
    }

    if (!orders || orders.length === 0) {
        return <div className="text-center py-5">No order found.</div>;
    }

    const order = orders[0]; // Assuming the first order is the confirmed one

    // Check if order is confirmed
    const isOrderConfirmed = order.paymentMethod === 'COD' || order.paymentStatus === 'Paid';

    // Determine transaction date: use paidDateUtc if available, else createdDateUtc
    const transactionDate = order.paidDateUtc || order.createdDateUtc;

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        let parsedDate = dateString;
        // Handle .NET JSON date format /Date(ticks)/
        if (typeof dateString === 'string' && dateString.startsWith('/Date(') && dateString.endsWith(')/')) {
            const ticks = parseInt(dateString.slice(6, -2));
            if (!isNaN(ticks)) {
                parsedDate = new Date(ticks);
            }
        }
        const date = new Date(parsedDate);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Format expected delivery date to show 6:00 PM - 9:00 PM
    const formatExpectedDeliveryDate = (dateString) => {
        if (!dateString) return 'N/A';
        let parsedDate = dateString;
        // Handle .NET JSON date format /Date(ticks)/
        if (typeof dateString === 'string' && dateString.startsWith('/Date(') && dateString.endsWith(')/')) {
            const ticks = parseInt(dateString.slice(6, -2));
            if (!isNaN(ticks)) {
                parsedDate = new Date(ticks);
            }
        }
        const date = new Date(parsedDate);
        if (isNaN(date.getTime())) return 'N/A';
        const datePart = date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        return `${datePart}, 6:00 PM - 9:00 PM`;
    };

    // Collect all tags from attributes
    const tags = [];
    if (order.products && order.products.length > 0) {
        const product = order.products[0];
        if (product.attributes) {
            // Add subProducts as tags
            if (product.attributes.subProducts) {
                product.attributes.subProducts.forEach(sub => {
                    tags.push(`${sub.name} ${sub.weight}`);
                });
            }
            // Add attributes as tags
            if (product.attributes.attributes) {
                product.attributes.attributes.forEach(attr => {
                    tags.push(attr.value);
                });
            }
        }
    }

    const handleOrderTracking = async (orderGuid) => {
        navigate("/order-tracking", { state: { orderGuid } });
    };

    return (
        <>
            <div className="col-xl-12">
                <div className={'stepper'}>
                    <div className={'stepper-content pt_42'}>
                        <div className="completion-screen">
                            <div className="completion-screen-content">
                                <div className="img-wrapper text-center">
                                    <img src={isOrderConfirmed ? PaymentSuccessIcon : WarningIcon} alt={isOrderConfirmed ? "Success" : "Failed"} className="success-icon" style={{ maxWidth: "30%" }} />
                                </div>
                                <h2 className={`fs_40 fw_700 ${isOrderConfirmed ? 'text-color' : 'text-danger'} mb_38 text-center`}>
                                    {isOrderConfirmed ? 'Order confirmed!' : 'Payment failed!'}
                                </h2>
                                <div className="complete-content">
                                    <div className={'d-flex mb_42'}>
                                        <div className="img-wrapper">
                                            <img className={'img-fluid'} src={getImageUrl(order.products[0].image, CartProductIcon)} onError={(e) => handleImageError(e, CartProductIcon)} alt="cart product" />
                                        </div>
                                        <div>
                                            <h6 className={'fs_24 fw_700 mb_12'}>
                                                {order.products && order.products.length > 0 ? `${order.products[0].productName} (${order.weight}KG)` : 'Product'}
                                            </h6>
                                            <div className={'tag-grid mb_26'}>
                                                {tags.map((tag, index) => (
                                                    <div key={index} className="tag">{tag}</div>
                                                ))}
                                            </div>
                                            <div className="content">
                                                <div>
                                                    <h5 className={'mb-0 fs_16 fw_500 text-color'}>Transaction Date</h5>
                                                    <h5 className={'mb-0 fs_16 fw_600 text-color'}>{formatDate(transactionDate)}</h5>
                                                </div>
                                                <div>
                                                    <h5 className={'mb-0 fs_16 fw_500 text-color'}>Expected Delivery Date</h5>
                                                    <h5 className={'mb-0 fs_16 fw_600 text-color'}>{formatExpectedDeliveryDate(order.expectedDeliveryDateUtc)}</h5>
                                                </div>
                                                <div>
                                                    <h5 className={'mb-0 fs_16 fw_500 text-color'}>Payment Method</h5>
                                                    <h5 className={'mb-0 fs_16 fw_600 text-color'}>{order.paymentMethod}</h5>
                                                </div>
                                                <div>
                                                    <h5 className={'mb-0 fs_16 fw_500 text-color'}>Subtotal</h5>
                                                    <h5 className={'mb-0 fs_16 fw_600 text-color'}>{order.totalPrice} ₹</h5>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="content out-side">
                                        <div>
                                            <h5 className={'mb-0 fs_16 fw_500 text-color'}>Transaction Date</h5>
                                            <h5 className={'mb-0 fs_16 fw_600 text-color'}>{formatDate(transactionDate)}</h5>
                                        </div>
                                        <div>
                                            <h5 className={'mb-0 fs_16 fw_500 text-color'}>Expected Delivery Date</h5>
                                            <h5 className={'mb-0 fs_16 fw_600 text-color'}>{formatExpectedDeliveryDate(order.expectedDeliveryDateUtc)}</h5>
                                        </div>
                                        <div>
                                            <h5 className={'mb-0 fs_16 fw_500 text-color'}>Payment Method</h5>
                                            <h5 className={'mb-0 fs_16 fw_600 text-color'}>{order.paymentMethod}</h5>
                                        </div>
                                        <div>
                                            <h5 className={'mb-0 fs_16 fw_500 text-color'}>Subtotal</h5>
                                            <h5 className={'mb-0 fs_16 fw_600 text-color'}>{order.totalPrice} ₹</h5>
                                        </div>
                                    </div>
                                    <div className={'text-center'}>
                                        <button onClick={(e) => handleOrderTracking(order.orderGuid)} className={'fs_16 fw_400 button btn-solid text-uppercase py_20 px_30'}>Track Your Order</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
export default OrderConfirmed;